import React from "react";
import {TweenLite, TweenMax, Animation, TimelineLite, Linear, Power1, Power4} from "gsap";
import styles from "./question.module.css";
import {QuestionSceneBase} from "./QuestionSceneBase";
import {QuestionSceneProps} from "./QuestionSceneProps";
import {AnimationSettingsProp} from "../../store/settings/types";
import icons from "../../shared/styles/icons.module.css";
import {shake} from "../../shared/utils/shake";
import {Overlay} from "../../shared/components/overlay/Overlay";
import {LoadingIndicator} from "../../shared/components/loading/LoadingIndicator";
import playSound from "../../shared/sounds/playSound";

type AnimatedQuestionSceneState = {
    lives: number;
    isLostLife: boolean;
    loading: boolean;
}

export class AnimatedQuestionScene extends QuestionSceneBase<AnimatedQuestionSceneState> {
    private activeTweens: Animation[] = [];
    private timerAnimation?: TweenLite;

    constructor(props: QuestionSceneProps, context: any) {
        super(props, context);

        this.state = {
            lives: props.lives,
            isLostLife: false,
            loading: false,
        };
    }

    componentDidMount(): void {
        super.componentDidMount();

        if (!this.hostElement.current)
            return;

        const {secondsForAnswer, isAnswered, animation} = this.props;

        if (!isAnswered) {
            const timer = this.hostElement.current.getElementsByClassName(styles.timer)[0];

            if (timer) {
                this.timerAnimation = TweenLite.to(timer, secondsForAnswer, {
                    width: 0,
                    ease: Linear.easeNone
                });
            }

            if (animation.enabled) {
                const image = this.hostElement.current.getElementsByClassName(styles.image);
                const question = this.hostElement.current.getElementsByClassName(styles.text);
                const answers = this.hostElement.current.getElementsByClassName(styles.answer);

                const timeline = new TimelineLite()
                    .from(image, 1.33, {
                        opacity: 0
                    }, 'first')
                    .from(question, 0.5, {
                        opacity: 0,
                        delay: 0.2,
                    }, 'first')
                    .staggerFrom(answers, 0.2, {
                        autoAlpha: 0,
                        x: -40,
                        delay: 0.4,
                        clearProps: "opacity",
                        onStart: () => playSound("answer-button-out"),
                    }, 0.1, 'first')
                    .timeScale(animation.timeScale);

                this.activeTweens.push(timeline);
            }
        }
    }

    componentDidUpdate(prevProps: Readonly<QuestionSceneProps & AnimationSettingsProp>, prevState: Readonly<AnimatedQuestionSceneState>, snapshot?: any): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        const {isAnswered, isTimeoutExpired, progress, animation} = this.props;

        if (!this.hostElement.current || !animation.enabled)
            return;

        const isTimeReset = (this.props.resetTime && this.props.resetTime !== prevProps.resetTime)
            || this.props.question.id !== prevProps.question.id;
        if (isTimeReset) {
            const timer = this.hostElement.current.getElementsByClassName(styles.timer)[0];

            if (timer) {
                this.timerAnimation && this.timerAnimation.kill();
                this.timerAnimation = TweenLite.fromTo(timer, this.props.secondsForAnswer, {
                    width: '100%'
                }, {
                    width: 0,
                    ease: Linear.easeNone
                });
            }
        }

        if (isAnswered && !prevProps.isAnswered) {
            this.timerAnimation && this.timerAnimation.pause();

            const stats = this.hostElement.current.getElementsByClassName(styles.stats);

            let tween = TweenLite.from(stats, 0.33, {
                width: 0,
                ease: Power1.easeInOut,
                onComplete: () => {
                    if (isTimeoutExpired || !progress.isRight) {
                        playSound("incorrect-answer");
                    } else {
                        playSound("correct-answer");
                    }
                }
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);

            const header = this.hostElement.current.getElementsByClassName(styles.text);
            const scrollView = this.hostElement.current.getElementsByClassName(styles.scroll_view);

            if (header.length) {
                tween = TweenLite.to(scrollView, 0.5, {
                    scrollTop: (header[0] as HTMLElement).offsetTop,
                    delay: 0.5,
                    ease: Power1.easeInOut
                }).timeScale(animation.timeScale);

                this.activeTweens.push(tween);
            }

            const explanation = this.hostElement.current.getElementsByClassName(styles.explanation);
            if (explanation.length) {
                tween = TweenLite.from(explanation, 0.5, {
                    opacity: 0,
                    delay: 0.5,
                    ease: Power1.easeInOut
                }).timeScale(animation.timeScale);

                this.activeTweens.push(tween);
            }
        }

        if (isAnswered && prevProps.lives > this.props.lives) {
            const lives = this.hostElement.current.parentElement!.getElementsByClassName(icons.lives);
            const livesCracked = this.hostElement.current.parentElement!.getElementsByClassName(icons.lives_cracked);

            const timeLine = new TimelineLite()
                .set(lives, { scale: 0, opacity: 0, zIndex: 100 })
                .set(livesCracked, { zIndex: 100 })
                .to(lives, 0.6, { scale: 1, opacity: 1, ease: Power4.easeOut }, "first")
                .add(shake(lives[0], 10, 5, 0.5), "first")
                .to(lives, 0.1, { opacity: 0 }, "second")
                .to(livesCracked, 0.1, { opacity: 1 }, "second")
                .add(TweenMax.to(livesCracked, 0.2, { scale: 1.2, repeat: 1, yoyo: true }), "second")
                .to(livesCracked, 1.0, { scale: 1.5, opacity: 0, ease: Power4.easeOut }, "third")
                .timeScale(animation.timeScale);

            this.activeTweens.push(timeLine);
        }
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }

        this.timerAnimation && this.timerAnimation.kill();
    }

    render() {
        const lives = this.state.isLostLife
            ? (
                <div className={styles.lives_overlay}>
                    <i className={`${icons.icon} ${icons.lives}`}/>
                    <i className={`${icons.icon} ${icons.lives_cracked}`}/>
                </div>
            )
            : undefined;

        const loading = this.state.loading
            ? (
                <Overlay opacity={0.3}>
                    <LoadingIndicator/>
                </Overlay>
            )
            : undefined;

        return (
            <>
                {super.render()}
                {lives}
                {loading}
            </>
        );
    }

    protected answer(answerId: number) {
        const {animation} = this.props;

        playSound("answer-select");

        if (!animation.enabled || !this.hostElement.current) {
            super.answer(answerId);
            return;
        }

        const answerIndex = this.props.question.answers.indexOf(answerId);
        const answers = this.hostElement.current.getElementsByClassName(styles.answer);

        const tween = TweenMax.fromTo(answers[answerIndex], 0.2, {
            scale: 1,
        }, {
            scale: 1.05,
            repeat: 1,
            yoyo: true,
            onStart: () => answers[answerIndex].classList.add(styles.selected_answer),
            onComplete: () => {
                answers[answerIndex].classList.remove(styles.selected_answer);
                super.answer(answerId);
            }
        }).timeScale(animation.timeScale);

        this.activeTweens.push(tween);
    }

    protected async share() {
        playSound("button-click");

        this.setState({
            loading: true
        });

        this.animateButtonClick(`.${styles.share_button}`, async () => {
            await super.share();

            this.setState({
                loading: false
            });
        });
    }

    protected continue() {
        const {leaveQuestion, progress} = this.props;

        playSound("button-click");

        this.animateButtonClick(`.${styles.continue_button}`, () => leaveQuestion(progress));
    }

    private animateButtonClick(selector: string, callback: () => void) {
        const {animation} = this.props;

        if (!animation.enabled || !this.hostElement.current) {
            return callback();
        }

        const element = this.hostElement.current.querySelector(selector);
        if (!element) {
            return callback();
        }

        const tween = TweenMax.fromTo(element, 0.1, {
            scale: 1,
        }, {
            scale: 1.05,
            repeat: 1,
            yoyo: true,
            onComplete: async () => {
                callback();
            }
        }).timeScale(animation.timeScale);

        this.activeTweens.push(tween);
    }

    static getDerivedStateFromProps(props: Readonly<QuestionSceneProps & AnimationSettingsProp>, state: Readonly<AnimatedQuestionSceneState>): AnimatedQuestionSceneState | null {
        if (props.lives !== state.lives) {
            return {
                lives: props.lives,
                isLostLife: true,
                loading: false,
            };
        }

        return null;
    }
}